import { useState } from 'react';
import {
  PLATFORM_CAPABILITIES,
  PLATFORM_CAPABILITY_LABELS,
  isPlatformCapabilityLive,
  type PlatformCapability,
} from '@gatecheckpro/contracts';
import {
  Button,
  Card,
  EmptyState,
  Field,
  LoadingRegion,
  Notice,
  Status,
} from '@gatecheckpro/design-system';
import { api } from '../lib/api.ts';
import { useResource } from '../lib/useResource.ts';
import { FailureNotice } from '../lib/refusal.tsx';
import { useViewer } from '../lib/session.tsx';

interface Grant {
  readonly grantId: string;
  readonly userId: string;
  readonly email: string;
  readonly displayName: string;
  readonly capability: PlatformCapability;
  readonly grantedAt: string;
}

/**
 * Administering CABS operations capabilities.
 *
 * The only power `OPS_PLATFORM_ADMIN` confers. It does not imply access to
 * organizer data, to onboarding, or to the audit trail — those are separate
 * grants, and an administrator can hand one out without being able to use it.
 */
export function CapabilityGrantsPage() {
  const viewer = useViewer();
  const { resource, reload } = useResource<{ grants: Grant[] }>('/api/v1/ops/capability-grants');

  const [email, setEmail] = useState('');
  const [capability, setCapability] = useState<PlatformCapability>('OPS_ORGANIZER_ONBOARDING');
  const [reasonCode, setReasonCode] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function grant(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.request('/api/v1/ops/capability-grants', {
        method: 'POST',
        body: { email, capability, reasonCode },
      });
      setEmail('');
      setReasonCode('');
      reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(grantId: string): Promise<void> {
    setError(null);
    try {
      await api.request(`/api/v1/ops/capability-grants/${grantId}`, {
        method: 'DELETE',
        body: { reasonCode: 'REVOKED_BY_ADMIN' },
      });
      reload();
    } catch (caught) {
      setError(caught);
    }
  }

  return (
    <div className="gc-stack gc-stack--loose">
      <div className="gc-page-header">
        <span className="gc-eyebrow">Platform administration</span>
        <h1>Capability grants</h1>
        <p className="gc-lede">
          There is no global operator. Each capability is granted to a named account, one at a time,
          with a reason, and takes effect on that person&rsquo;s next request.
        </p>
      </div>

      <FailureNotice error={error} />

      <Card title="Grant a capability">
        <form className="gc-stack" onSubmit={(event) => void grant(event)}>
          <Field
            label="Email address"
            type="email"
            required
            hint="The person must already have a GateCheckPro account. Granting to an unclaimed address would hand the capability to whoever registers it next."
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />

          <div className="gc-field">
            <label className="gc-field__label" htmlFor="capability">
              Capability
            </label>
            <select
              id="capability"
              className="gc-select"
              value={capability}
              onChange={(event) => setCapability(event.target.value as PlatformCapability)}
            >
              {PLATFORM_CAPABILITIES.map((value) => (
                <option key={value} value={value}>
                  {PLATFORM_CAPABILITY_LABELS[value]}
                  {isPlatformCapabilityLive(value) ? '' : ' — no tooling yet'}
                </option>
              ))}
            </select>
          </div>

          <Field
            label="Reason"
            required
            placeholder="ONBOARDING_ROTA"
            hint="Recorded permanently against the grant."
            value={reasonCode}
            onChange={(event) => setReasonCode(event.target.value)}
          />

          <Button
            type="submit"
            variant="primary"
            busy={busy}
            disabled={email.length === 0 || reasonCode.length < 2}
          >
            Grant
          </Button>
        </form>
      </Card>

      {resource.status === 'loading' && <LoadingRegion label="Loading grants" />}
      {resource.status === 'failed' && <FailureNotice error={resource.error} />}

      {resource.status === 'loaded' &&
        (resource.data.grants.length === 0 ? (
          <EmptyState title="No capabilities are granted">
            Nobody currently holds an operations capability.
          </EmptyState>
        ) : (
          <div className="gc-table-scroll">
            <table className="gc-table">
              <thead>
                <tr>
                  <th scope="col">Person</th>
                  <th scope="col">Capability</th>
                  <th scope="col">Granted</th>
                  <th scope="col">
                    <span className="gc-visually-hidden">Revoke</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {resource.data.grants.map((row) => (
                  <tr key={row.grantId}>
                    <td>
                      <div className="gc-stack gc-stack--tight">
                        <strong>{row.displayName}</strong>
                        <span className="gc-muted">{row.email}</span>
                      </div>
                    </td>
                    <td>
                      <Status
                        tone={isPlatformCapabilityLive(row.capability) ? 'info' : 'neutral'}
                        label={PLATFORM_CAPABILITY_LABELS[row.capability]}
                        title={
                          isPlatformCapabilityLive(row.capability)
                            ? 'Usable now.'
                            : 'Granted, but no tooling consumes it in this release.'
                        }
                      />
                    </td>
                    <td className="gc-mono">{new Date(row.grantedAt).toLocaleString()}</td>
                    <td>
                      {/*
                        Revoking your own administration capability is the one
                        move that cannot be undone from inside the product.
                      */}
                      {row.userId === viewer.userId && row.capability === 'OPS_PLATFORM_ADMIN' ? (
                        <span className="gc-muted">Your own admin grant</span>
                      ) : (
                        <Button variant="destructive" onClick={() => void revoke(row.grantId)}>
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

      {viewer.platformCapabilities.length > 0 && (
        <Notice tone="info" title="What you hold">
          {viewer.platformCapabilities.map((held) => PLATFORM_CAPABILITY_LABELS[held]).join(', ')}.
          Administering grants does not give you the capabilities you administer.
        </Notice>
      )}
    </div>
  );
}
